import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

export type CampaignQueueSourceModule = 'referral' | 'cold_outreach' | 'newsfeed' | 'collaborations' | 'events' | 'podcast' | 'lms';

export interface CampaignQueueItem {
  id: string;
  brand_id: string;
  source_module: CampaignQueueSourceModule;
  campaign_id: string;
  campaign_email_id: string;
  recipient_id: string | null;
  recipient_email: string;
  recipient_name: string | null;
  recipient_data: Record<string, unknown>;
  step_order: number;
  delay_days: number;
  delay_hours: number;
  status: 'pending' | 'scheduled' | 'queued' | 'sent' | 'skipped' | 'failed' | 'cancelled';
  enrolled_at: string;
  scheduled_at: string;
  queued_at: string | null;
  sent_at: string | null;
  error_message: string | null;
  metadata: Record<string, unknown>;
  created_at: string;
  updated_at: string;
}

export function useCampaignQueue(
  sourceModule: CampaignQueueSourceModule,
  brandId: string | null,
  campaignId?: string | null,
  limit = 100
) {
  return useQuery({
    queryKey: ['campaign-queue', sourceModule, brandId, campaignId, limit],
    queryFn: async () => {
      if (!brandId) return [];

      let query = supabase
        .from('os_campaign_queue')
        .select('*')
        .eq('brand_id', brandId)
        .eq('source_module', sourceModule)
        .order('scheduled_at', { ascending: true })
        .limit(limit);

      if (campaignId) {
        query = query.eq('campaign_id', campaignId);
      }

      const { data, error } = await query;

      if (error) throw error;
      return (data || []) as CampaignQueueItem[];
    },
    enabled: !!brandId,
  });
}

export interface CampaignQueueSummary {
  total_pending: number;
  total_scheduled: number;
  total_queued: number;
  total_sent: number;
  total_skipped: number;
  total_failed: number;
  next_scheduled_at: string | null;
}

export function useCampaignQueueSummary(
  sourceModule: CampaignQueueSourceModule,
  brandId: string | null,
  campaignId?: string | null
) {
  return useQuery({
    queryKey: ['campaign-queue-summary', sourceModule, brandId, campaignId],
    queryFn: async (): Promise<CampaignQueueSummary> => {
      if (!brandId) {
        return {
          total_pending: 0,
          total_scheduled: 0,
          total_queued: 0,
          total_sent: 0,
          total_skipped: 0,
          total_failed: 0,
          next_scheduled_at: null,
        };
      }

      let query = supabase
        .from('os_campaign_queue')
        .select('status, scheduled_at')
        .eq('brand_id', brandId)
        .eq('source_module', sourceModule);

      if (campaignId) {
        query = query.eq('campaign_id', campaignId);
      }

      const { data: queueData, error: queueError } = await query;

      if (queueError) throw queueError;

      const items = queueData || [];
      const pendingItems = items.filter(q => q.status === 'pending');
      
      const stats: CampaignQueueSummary = {
        total_pending: pendingItems.length,
        total_scheduled: items.filter(q => q.status === 'scheduled').length,
        total_queued: items.filter(q => q.status === 'queued').length,
        total_sent: items.filter(q => q.status === 'sent').length,
        total_skipped: items.filter(q => q.status === 'skipped').length,
        total_failed: items.filter(q => q.status === 'failed').length,
        next_scheduled_at: pendingItems.length > 0 
          ? pendingItems.sort((a, b) => new Date(a.scheduled_at).getTime() - new Date(b.scheduled_at).getTime())[0].scheduled_at 
          : null,
      };

      return stats;
    },
    enabled: !!brandId,
  });
}

export function useCancelCampaignQueueItem() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from('os_campaign_queue')
        .update({ status: 'cancelled', updated_at: new Date().toISOString() })
        .eq('id', id);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['campaign-queue'] });
      queryClient.invalidateQueries({ queryKey: ['campaign-queue-summary'] });
    },
  });
}

export function useEnrollLeadInCampaign() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      brandId,
      sourceModule,
      campaignId,
      recipientId,
      recipientEmail,
      recipientName,
      recipientData,
      campaignEmails,
    }: {
      brandId: string;
      sourceModule: CampaignQueueSourceModule;
      campaignId: string;
      recipientId: string | null;
      recipientEmail: string;
      recipientName: string | null;
      recipientData: Record<string, unknown>;
      campaignEmails: Array<{
        id: string;
        sequence_order: number;
        delay_days: number;
        delay_hours: number;
        send_conditions?: Record<string, unknown> | null;
      }>;
    }) => {
      // STEP-BY-STEP: Only insert the FIRST email step (sequence_order = 1)
      // The universal-campaign-queue-processor will create subsequent steps after conditions are checked
      const firstEmail = campaignEmails.find(e => e.sequence_order === 1) || campaignEmails[0];
      if (!firstEmail) {
        console.warn('No campaign emails provided for enrollment');
        return;
      }

      const enrolledAt = new Date();
      const scheduledAt = new Date(enrolledAt);
      scheduledAt.setDate(scheduledAt.getDate() + firstEmail.delay_days);
      scheduledAt.setHours(scheduledAt.getHours() + firstEmail.delay_hours);

      const queueItem = {
        brand_id: brandId,
        source_module: sourceModule,
        campaign_id: campaignId,
        campaign_email_id: firstEmail.id,
        recipient_id: recipientId,
        recipient_email: recipientEmail,
        recipient_name: recipientName,
        recipient_data: recipientData as Record<string, string | number | boolean | null>,
        step_order: firstEmail.sequence_order,
        delay_days: firstEmail.delay_days,
        delay_hours: firstEmail.delay_hours,
        status: 'pending' as const,
        enrolled_at: enrolledAt.toISOString(),
        scheduled_at: scheduledAt.toISOString(),
        metadata: JSON.parse(JSON.stringify(firstEmail.send_conditions ? { stop_conditions: firstEmail.send_conditions } : {})),
      };

      const { error } = await supabase
        .from('os_campaign_queue')
        .upsert([queueItem], { onConflict: 'campaign_email_id,recipient_email' });

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['campaign-queue'] });
      queryClient.invalidateQueries({ queryKey: ['campaign-queue-summary'] });
    },
  });
}
